"use client";

import { Pencil } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type AdminEditableTextProps = {
  isAdmin: boolean;
  title: string;
  description?: string;
  value: string;
  multiline?: boolean;
  extra?: ReactNode;
  onSave: (next: string) => Promise<string | null>;
  children: ReactNode;
};

export function AdminEditableText({
  isAdmin,
  title,
  description,
  value,
  multiline = true,
  extra,
  onSave,
  children,
}: AdminEditableTextProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  if (!isAdmin) return <>{children}</>;

  const openEditor = () => {
    setDraft(value);
    setStatus(null);
    setIsOpen(true);
  };

  const save = async () => {
    setIsSaving(true);
    setStatus(null);
    try {
      const error = await onSave(draft);
      if (error) {
        setStatus(error);
        return;
      }
      setIsOpen(false);
    } catch {
      setStatus("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative">
      {children}
      <button
        type="button"
        onClick={openEditor}
        className="absolute -right-1 -top-1 z-20 h-8 w-8 rounded-md inline-flex items-center justify-center text-[#555]/70 hover:text-[#555] hover:bg-white/80"
        aria-label={title}
      >
        <Pencil className="h-4 w-4" />
      </button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          {multiline ? (
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="min-h-32"
            />
          ) : (
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          )}
          {extra}
          {status && <p className="text-xs text-red-600">{status}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
